import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Eye, Trash2, Loader2, Monitor } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { useTvTemplates, TvTemplate } from '@/hooks/useTvTemplates'
import { tvTemplateService } from '@/services/tv_template_service'
import { useViewMode } from '@/stores/viewMode'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import CreateTemplateDialog from './CreateTemplateDialog'

export default function TvTemplatesPage() {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<TvTemplate | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const providerId = mode === 'provider' && viewModeId ? viewModeId : undefined
  const { templates, isLoading, mutate } = useTvTemplates({ providerId })

  const handleDelete = (template: TvTemplate) => {
    setTemplateToDelete(template)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!templateToDelete || !token) return

    setIsDeleting(true)
    try {
      const result = await tvTemplateService.deleteTemplate(
        templateToDelete.template_id,
        token,
      )
      if (result.success) {
        mutate()
      }
    } catch {
      toast.error('刪除失敗')
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setTemplateToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-96 mt-2" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="工具箱"
        title="電視模板管理"
        description="管理電視 VOD 配置模板，批次套用到多台設備"
        icon={<Monitor className="h-8 w-8" />}
        level="section"
      >
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          新增模板
        </Button>
      </PageHeader>

      <Card className="p-6">
        {templates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">尚未建立任何模板</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>模板名稱</TableHead>
                <TableHead>VOD 數量</TableHead>
                <TableHead>設備數量</TableHead>
                <TableHead>建立時間</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.template_id}>
                  <TableCell className="font-medium">{template.template_name}</TableCell>
                  <TableCell>{template._count?.template_vods ?? 0}</TableCell>
                  <TableCell>{template._count?.devices ?? 0}</TableCell>
                  <TableCell>
                    {new Date(template.created_at).toLocaleDateString('zh-TW')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const prefix =
                            mode === 'provider' && viewModeId ? `/provider/${viewModeId}` : '/admin'
                          navigate(`${prefix}/apps/tv-templates/${template.template_id}`)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(template)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateTemplateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          mutate()
          setCreateDialogOpen(false)
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此模板嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作無法復原。這將永久刪除模板 &quot;{templateToDelete?.template_name}&quot;。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                '確認刪除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
